import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPurchaseByTransType } from "../../../services/PurchaseApiService";
import { fetchProduct, fetchSupplier } from "../../../redux/userSetting";
import { fetchBranchSettings } from "../../../redux/settingbyBranch";
import { getWareHouseList } from "../../../services/MasterApiServices";
import { ReportScreen } from "../../../components/report";
import {
  dateOf,
  dmy,
  groupSum,
  intg,
  money,
  num,
  sumBy,
} from "../../../components/report/reportFormat";

// the payment map this report already used for its Status column
const PAYMENTS = [
  { v: 0, t: "Cash", tone: "cash" },
  { v: 1, t: "Card", tone: "card" },
  { v: 2, t: "Bank", tone: "upi" },
  { v: 3, t: "Credit", tone: "credit" },
  { v: 4, t: "UPI", tone: "upi" },
];

const PurchaseReturnReport = () => {
  const dispatch = useDispatch();
  const { branch } = useSelector((state) => state.branchlist);
  const { formatedproductList, formatedsupplierList } = useSelector(
    (state) => state.usersetting
  );

  const [purchaseData, setPurchaseData] = useState([]);
  const [wareHouseList, setWareHouseList] = useState([]);

  const handlePurchaseData = async () => {
    try {
      const response = await getPurchaseByTransType(branch, 3);
      setPurchaseData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to fetch purchase returns", error);
    }
  };

  useEffect(() => {
    dispatch(fetchSupplier());
  }, []);

  useEffect(() => {
    dispatch(fetchProduct(branch));
    dispatch(fetchBranchSettings(branch.guid));
    handlePurchaseData();
  }, [branch]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await getWareHouseList();
        setWareHouseList(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error("Failed to fetch warehouses:", err);
      }
    };
    fetchWarehouses();
  }, []);

  const warehouseMap = useMemo(
    () => Object.fromEntries(wareHouseList.map((w) => [w.guid, w.name || "Unnamed Warehouse"])),
    [wareHouseList]
  );

  /* one flat record per purchase return */
  const rows = useMemo(
    () =>
      purchaseData.map((inv) => {
        const pay = PAYMENTS.find((p) => p.v === inv.purchasePaymentType);
        return {
          guid: inv.guid,
          date: dateOf(inv.date),
          billNo: inv.entrynumber || "",
          supplier: inv.supplier?.name || "Unknown Supplier",
          supplierGuid: inv.supplier?.guid,
          invoiceNo: inv.invoiceNo || "",
          invoiceDate: dateOf(inv.invoiceDate),
          warehouse: warehouseMap[inv.wareHouse?.guid] || inv.wareHouse?.name || "-",
          warehouseGuid: inv.wareHouse?.guid,
          payment: pay?.t || "",
          paymentType: inv.purchasePaymentType,
          total: num(inv.grossTotal),
          discount: num(inv.discountTotal),
          tax: num(inv.taxTotal),
          net: num(inv.netTotal),
          lines: inv.purchaseDetails || [],
        };
      }),
    [purchaseData, warehouseMap]
  );

  /* ---------------- filters ---------------- */
  const filters = useMemo(
    () => [
      {
        key: "products",
        label: "Products",
        items: (formatedproductList || []).map((p) => ({ v: p.value, t: p.label || "—" })),
        match: (r, v) => r.lines.some((l) => v.includes(l?.product?.guid)),
      },
      {
        key: "suppliers",
        label: "Suppliers",
        items: (formatedsupplierList || []).map((s) => ({ v: s.value, t: s.label || "—" })),
        match: (r, v) => v.includes(r.supplierGuid),
      },
      {
        key: "warehouses",
        label: "Warehouses",
        items: wareHouseList.map((w) => ({ v: w.guid, t: w.name || "Unnamed Warehouse" })),
        match: (r, v) => v.includes(r.warehouseGuid),
      },
    ],
    [formatedproductList, formatedsupplierList, wareHouseList]
  );

  const searchMatch = (r, q) => {
    const hit = (s) => String(s || "").toLowerCase().includes(q);
    return (
      hit(r.billNo) ||
      hit(r.supplier) ||
      hit(r.invoiceNo) ||
      hit(r.warehouse) ||
      hit(r.date) ||
      hit(r.payment) ||
      String(r.net).includes(q) ||
      r.lines.some((l) => hit(l?.product?.name) || hit(l?.product?.code))
    );
  };

  /* ---------------- tabs ---------------- */
  const tabs = useMemo(
    () => [
      {
        id: "detail",
        label: "Detailed report",
        defaultSort: { k: "date", d: -1 },
        rowKey: (r) => r.guid,
        groupOptions: [
          { v: "supplier", t: "Supplier" },
          { v: "warehouse", t: "Warehouse" },
          { v: "payment", t: "Payment" },
          { v: "__month", t: "Month" },
        ],
        columns: [
          { k: "sl", t: "Sl No", type: "index" },
          { k: "date", t: "Date", type: "date" },
          { k: "billNo", t: "Bill No", type: "code" },
          { k: "supplier", t: "Supplier", type: "avatar" },
          { k: "warehouse", t: "Warehouse" },
          { k: "invoiceNo", t: "Invoice No", off: true },
          { k: "invoiceDate", t: "Invoice Date", type: "date", off: true },
          {
            k: "payment",
            t: "Status",
            type: "pill",
            pill: (r) => ({ text: r.payment, tone: PAYMENTS.find((p) => p.v === r.paymentType)?.tone }),
          },
          { k: "total", t: "Total", type: "money" },
          { k: "discount", t: "Discount", type: "money" },
          { k: "tax", t: "Tax", type: "money" },
          { k: "net", t: "Net Amount", type: "money" },
        ],
      },
      {
        id: "supplier",
        label: "Supplier",
        defaultSort: { k: "net", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) => {
          const g = groupSum(
            list,
            (r) => r.supplierGuid || r.supplier,
            ["total", "discount", "tax", "net"],
            (r) => ({ supplier: r.supplier, last: "" })
          );
          g.forEach((row) => {
            row.last = list
              .filter((r) => (r.supplierGuid || r.supplier) === row.key)
              .reduce((a, r) => (r.date > a ? r.date : a), "");
            row.avg = row.n ? row.net / row.n : 0;
          });
          return g;
        },
        columns: [
          { k: "supplier", t: "Supplier", type: "avatar" },
          { k: "n", t: "Returns", type: "int" },
          { k: "total", t: "Total", type: "money" },
          { k: "discount", t: "Discount", type: "money" },
          { k: "tax", t: "Tax", type: "money" },
          { k: "net", t: "Net Amount", type: "money" },
          { k: "avg", t: "Avg Return", type: "money", noSum: true },
          { k: "last", t: "Last Return Date", type: "date" },
          { k: "share", t: "Share of Net", type: "share", shareOf: "net" },
        ],
      },
      {
        id: "item",
        label: "Item-wise",
        defaultSort: { k: "net", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) => {
          const m = {};
          list.forEach((r) =>
            r.lines.forEach((l) => {
              const k = l?.product?.guid || l?.product?.name || "unknown";
              if (!m[k])
                m[k] = {
                  key: k,
                  code: l?.product?.code || "—",
                  name: l?.product?.name || "Unknown Item",
                  unit: l?.unit?.name || "",
                  n: 0,
                  qty: 0,
                  total: 0,
                  discount: 0,
                  tax: 0,
                  net: 0,
                };
              const g = m[k];
              const qty = num(l.quantity);
              g.n += 1;
              g.qty += qty;
              g.total += num(l.grossAmount ?? qty * num(l.price));
              g.discount += num(l.discountAmount);
              g.tax += num(l.taxAmount);
              g.net += num(l.netAmount);
            })
          );
          return Object.values(m).map((g) => ({ ...g, avg: g.qty ? g.net / g.qty : 0 }));
        },
        columns: [
          { k: "code", t: "Item Code", type: "code" },
          { k: "name", t: "Item Name" },
          { k: "unit", t: "Unit", off: true },
          { k: "n", t: "Lines", type: "int" },
          { k: "qty", t: "Quantity", type: "int" },
          { k: "total", t: "Total", type: "money" },
          { k: "discount", t: "Discount", type: "money" },
          { k: "tax", t: "Tax", type: "money" },
          { k: "net", t: "Net Amount", type: "money" },
          { k: "avg", t: "Avg / Unit", type: "money", noSum: true, off: true },
          { k: "share", t: "Share of Net", type: "share", shareOf: "net" },
        ],
      },
      {
        id: "daily",
        label: "Day-wise",
        defaultSort: { k: "date", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) => {
          const g = groupSum(list, (r) => r.date, ["total", "discount", "tax", "net"]);
          return g.map((row) => ({ ...row, date: row.key, avg: row.n ? row.net / row.n : 0 }));
        },
        columns: [
          { k: "date", t: "Date", type: "date" },
          { k: "n", t: "Returns", type: "int" },
          { k: "total", t: "Total", type: "money" },
          { k: "discount", t: "Discount", type: "money" },
          { k: "tax", t: "Tax", type: "money" },
          { k: "net", t: "Net Amount", type: "money" },
          { k: "avg", t: "Avg Return", type: "money", noSum: true },
          { k: "share", t: "Share of Net", type: "share", shareOf: "net" },
        ],
      },
    ],
    []
  );

  /* ---------------- KPIs ---------------- */
  const kpis = useMemo(
    () => [
      { label: "Returns", value: (list) => list.length, format: "int" },
      { label: "Gross returned", value: (list) => sumBy(list, "total") },
      { label: "Discount", value: (list) => sumBy(list, "discount"), invert: true },
      { label: "Tax", value: (list) => sumBy(list, "tax") },
      { label: "Net returned", value: (list) => sumBy(list, "net"), hero: true, invert: true },
      {
        label: "Average return",
        value: (list) => (list.length ? sumBy(list, "net") / list.length : 0),
      },
    ],
    []
  );

  /* ---------------- drill-down ---------------- */
  const drawerContent = (r) => ({
    title: r.supplier,
    subtitle: `${r.billNo} · ${dmy(r.date)}`,
    avatarKey: r.supplier,
    meta: [
      { k: "Warehouse", v: r.warehouse },
      { k: "Invoice No", v: r.invoiceNo },
      { k: "Invoice Date", v: r.invoiceDate ? dmy(r.invoiceDate) : "—" },
      { k: "Status", v: r.payment },
      { k: "Lines", v: intg((r.lines || []).length) },
    ],
    lines: {
      title: "Return lines",
      columns: [
        { t: "Item" },
        { t: "Qty", align: "right" },
        { t: "Rate", align: "right" },
        { t: "Disc", align: "right" },
        { t: "Amount", align: "right" },
      ],
      rows: (r.lines || []).map((l) => [
        <>
          <div className="axr-nm">{l?.product?.name || "Unknown Item"}</div>
          <div className="axr-cd">{[l?.product?.code, l?.unit?.name].filter(Boolean).join(" · ")}</div>
        </>,
        intg(l?.quantity),
        money(l?.price),
        num(l?.discountAmount) ? money(l.discountAmount) : "—",
        money(l?.netAmount),
      ]),
    },
    totals: [
      { l: "Gross", v: money(r.total) },
      { l: "Discount", v: r.discount ? "−" + money(r.discount) : "0.00" },
      { l: "Tax", v: money(r.tax) },
    ],
    net: { l: "Net amount", v: money(r.net) },
  });

  return (
    <ReportScreen
      title="Purchase Return Report"
      subtitle="Every purchase return raised in the selected period"
      unit="returns"
      rows={rows}
      tabs={tabs}
      filters={filters}
      kpis={kpis}
      chart={{ valueKey: "net", title: "Net returns over time" }}
      searchMatch={searchMatch}
      searchPlaceholder="Bill no, supplier, item, invoice no…"
      exportName="PurchaseReturn"
      reportNames={{
        detail: "Purchase Return Detailed Reports",
        supplier: "Purchase Return Supplier Reports",
        item: "Purchase Return Itemwise Reports",
        daily: "Purchase Return Day Wise",
      }}
      onRefresh={handlePurchaseData}
      drawerContent={drawerContent}
    />
  );
};

export default PurchaseReturnReport;
